import { type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
  createApi,
  fetchBaseQuery,
  retry, } from "@reduxjs/toolkit/query/react";

import { ALL_API_TAGS }                   from "src/constants/api-tags";
import { API_BASE_URL, ENDPOINTS }        from "src/constants/api/api";
import { logoutSuccess }                  from "src/store/slices/auth";
import { getAccessToken, setAccessToken } from "src/utils/token-storage";

// Create our baseQuery instance
const baseQuery = fetchBaseQuery({
  baseUrl    : API_BASE_URL,
  credentials: "include", // Include cookies in all requests

  prepareHeaders: (headers) => {
    // By default, if we have a token in memory, let's use that for authenticated requests
    const token = getAccessToken();
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }

    return headers;
  },
});

const baseQueryWithRetry = retry(baseQuery, { maxRetries: 0 });

export const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  let result = await baseQueryWithRetry(args, api, extraOptions);

  // Handle Nginx 413 Request Entity Too Large error
  if (result.error && result.error.status === 413) {
    return {
      ...result,
      error: {
        ...result.error,
        data: { message: "The selected file is too large. Maximum allowed size is 5 MB." },
      },
    };
  }

  // Handle Unauthorized error - try to refresh token
  // But don't try to refresh if this IS the refresh endpoint to prevent infinite loops
  if (result.error && result.error.status === 401) {
    const url = typeof args === "string" ? args : args.url;
    
    // Skip reauth logic for refresh endpoint to prevent infinite loops
    if (url === ENDPOINTS.AUTH.REFRESH) {
      // Refresh failed, logout user
      api.dispatch(logoutSuccess());

      return result;
    }

    const refreshResult = await baseQueryWithRetry({
        url        : ENDPOINTS.AUTH.REFRESH,
        method     : "POST",
        credentials: "include",
      },
      api,
      extraOptions);

    if (refreshResult.data) {
      // Store new access token in memory
      const newToken = (refreshResult.data as any).accessToken;
      setAccessToken(newToken);

      // Retry the original request with new token
      result = await baseQueryWithRetry(args, api, extraOptions);
    } else {
      // Refresh failed, logout user
      api.dispatch(logoutSuccess());
    }
  }

  return result;
};

/**
 * Create a base API to inject endpoints into elsewhere.
 * Components using this API should import from the injected site,
 * in order to get the appropriate types,
 * and to ensure that the file injecting the endpoints is loaded
 */
export const api = createApi({
  refetchOnReconnect: true,

  /**
   * `reducerPath` is optional and will not be required by most users.
   * This is useful if you have multiple API definitions,
   * e.g. where each has a different domain, with no interaction between endpoints.
   * Otherwise, a single API definition should be used in order to support tag invalidation,
   * among other features
   */
  reducerPath: "api",
  /**
   * A bare bones base query would just be `baseQuery: fetchBaseQuery({ baseUrl: '/' })`
   */
  baseQuery  : baseQueryWithReauth,
  /**
   * Tag types must be defined in the original API definition
   * for any tags that would be provided by injected endpoints
   */
  tagTypes   : ALL_API_TAGS,
  /**
   * This api has endpoints injected in adjacent files,
   * which is why no endpoints are shown below.
   * If you want all endpoints defined in the same file, they could be included here instead
   */
  endpoints  : () => ({}),
});
